import { Link } from "@inertiajs/react"

import AdminLayout from "../../../shared/AdminLayout"

interface AdminFlightRow {
  id: number
  // "who flies · flight number" -- Flights::AdminFlightsController#row_props.
  title: string
  // "times (arrival date) · route" -- the same controller.
  detailsText: string
  editHref: string
}

interface AdminFlightSection {
  heading: string
  rows: AdminFlightRow[]
}

interface AdminFlightsProps {
  sections: AdminFlightSection[]
  emptyText: string
  newHref: string
  addLabel: string
}

const ROW_CLASSES = "flex items-center gap-3 border-b border-gray-100 py-3"
const LAST_ROW_CLASSES = "flex items-center gap-3 py-3"

// The admin flights list (`Flights::AdminFlightsController#index`,
// `/admin/flights`). Implements docs/design/flights.html's Block 3
// verbatim: the same by_departure order as the public page, grouped into
// date sections (FR-6.3), rows tap-to-edit in the landed admin idiom (one
// interactive zone, a chevron, no other control -- the
// `contacts/AdminContacts` shape verbatim), and a dashed «+ Добавить
// рейс» at the bottom -- present even with zero flights, when the
// localized empty state renders in its place above it rather than any
// section chrome.
export default function AdminFlights({ sections, emptyText, newHref, addLabel }: AdminFlightsProps) {
  return (
    <AdminLayout mainClassName="flex flex-col gap-6 p-4">
      {sections.length > 0 ? (
        sections.map((section) => (
          <section key={section.heading} className="flex flex-col gap-1">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {section.heading}
            </h2>
            <ol className="flex flex-col">
              {section.rows.map((row, index) => (
                <li key={row.id}>
                  <Link
                    href={row.editHref}
                    className={index === section.rows.length - 1 ? LAST_ROW_CLASSES : ROW_CLASSES}
                  >
                    <div className="flex grow flex-col gap-px">
                      <div className="text-[15px] font-semibold leading-[22px] text-gray-900">
                        {row.title}
                      </div>
                      <div className="text-[13px] leading-[18px] text-gray-500 tabular-nums">
                        {row.detailsText}
                      </div>
                    </div>
                    <svg
                      aria-hidden="true"
                      className="text-gray-400"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M6 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </li>
              ))}
            </ol>
          </section>
        ))
      ) : (
        <p className="px-6 py-16 text-center text-[15px] text-gray-500">{emptyText}</p>
      )}
      <Link
        href={newHref}
        className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-gray-300 text-sm font-medium text-accent"
      >
        {addLabel}
      </Link>
    </AdminLayout>
  )
}
